<template>
  <Dialog
    :visible="visible"
    :header="dialogTitle"
    :modal="true"
    class="nomenclature-item-dialog min-w-[75vw] max-w-[90vw]"
    :pt="dialogHeaderPt"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="nomenclature-item-dialog__body">
      <!-- Sub-item parent indicator -->
      <div v-if="parentName" class="nomenclature-item-dialog__parent-indicator">
        <i class="pi pi-sitemap mr-2"></i>
        <span>
          {{ t("views.nomenclatures.dialog.subItemOf") }}
          <strong>{{ parentName }}</strong>
        </span>
      </div>

      <div class="nomenclature-item-dialog__field">
        <label class="nomenclature-item-dialog__label">
          {{ t("views.nomenclatures.dialog.fields.type.label") }}
          <span class="nomenclature-item-dialog__required">*</span>
        </label>
        <Select
          v-model="form.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('views.nomenclatures.dialog.fields.type.placeholder')"
          :disabled="!!editItem || !!parentId"
          size="small"
          class="w-full"
        />
        <small v-if="errors.type" class="nomenclature-item-dialog__error">
          {{ errors.type }}
        </small>
      </div>

      <div class="nomenclature-item-dialog__field">
        <label class="nomenclature-item-dialog__label">
          {{ t("views.nomenclatures.dialog.fields.name.label") }}
          <span class="nomenclature-item-dialog__required">*</span>
        </label>
        <InputText
          v-model="form.name"
          :placeholder="t('views.nomenclatures.dialog.fields.name.placeholder')"
          size="small"
          class="w-full"
          @keyup.enter="handleSave"
        />
        <small v-if="errors.name" class="nomenclature-item-dialog__error">
          {{ errors.name }}
        </small>
      </div>

      <div class="nomenclature-item-dialog__field">
        <label class="nomenclature-item-dialog__label">
          {{ t("views.nomenclatures.dialog.fields.code.label") }}
        </label>
        <InputText
          v-model="form.code"
          :placeholder="t('views.nomenclatures.dialog.fields.code.placeholder')"
          size="small"
          class="w-full"
        />
      </div>

      <!-- Dynamic type properties -->
      <template v-if="typeProperties.length > 0">
        <div class="nomenclature-item-dialog__divider" />
        <div
          v-for="prop in typeProperties"
          :key="prop.key"
          class="nomenclature-item-dialog__field"
        >
          <label class="nomenclature-item-dialog__label">
            {{ prop.label }}
            <span
              v-if="prop.required"
              class="nomenclature-item-dialog__required"
              >*</span
            >
          </label>
          <Textarea
            v-if="prop.type === 'textarea'"
            v-model="form.propertyValues[prop.key]"
            :placeholder="prop.label"
            size="small"
            rows="2"
            auto-resize
            class="w-full"
          />
          <InputNumber
            v-else-if="prop.type === 'number'"
            v-model="form.propertyValues[prop.key]"
            :placeholder="prop.label"
            size="small"
            class="w-full"
          />
          <ToggleSwitch
            v-else-if="prop.type === 'boolean'"
            v-model="form.propertyValues[prop.key]"
          />
          <InputText
            v-else
            v-model="form.propertyValues[prop.key]"
            :placeholder="prop.label"
            size="small"
            class="w-full"
          />
          <small v-if="prop.hint" class="nomenclature-item-dialog__hint">
            {{ prop.hint }}
          </small>
        </div>
      </template>

      <div class="nomenclature-item-dialog__field">
        <label class="nomenclature-item-dialog__label">
          {{ t("views.nomenclatures.dialog.fields.notes.label") }}
        </label>
        <Textarea
          v-model="form.notes"
          :placeholder="
            t('views.nomenclatures.dialog.fields.notes.placeholder')
          "
          size="small"
          rows="2"
          auto-resize
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <div class="nomenclature-item-dialog__footer">
        <div class="flex items-center gap-2">
          <label class="text-sm">
            {{ t("views.nomenclatures.dialog.fields.status.label") }}
          </label>
          <ToggleSwitch v-model="form.status" input-id="nomStatus" />
          <label for="nomStatus" class="text-sm">
            {{
              form.status
                ? t("views.nomenclatures.dialog.fields.status.active")
                : t("views.nomenclatures.dialog.fields.status.inactive")
            }}
          </label>
        </div>

        <div class="flex gap-2">
          <Button
            :label="t('common.cancel')"
            severity="secondary"
            size="small"
            :disabled="saving"
            @click="$emit('update:visible', false)"
          />
          <Button
            :label="editItem ? t('common.update') : t('common.save')"
            class="bg-[linear-gradient(135deg,#2b3990,#2699d5)] hover:opacity-90 active:opacity-95 transition-opacity duration-200 border-none disabled:opacity-50 disabled:hover:opacity-50 disabled:active:opacity-50"
            size="small"
            :loading="saving"
            :disabled="!isValid || saving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue"
import { useI18n } from "vue-i18n"
import { dialogHeaderPt } from "@/config/dialogPt"
import { useNomenclaturesStore } from "@/store/nomenclatures"

const { t } = useI18n()
const nomenclaturesStore = useNomenclaturesStore()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  editItem: {
    type: Object,
    default: null
  },
  nomenclatureType: {
    type: String,
    default: null
  },
  availableTypes: {
    type: Array,
    default: () => []
  },
  parentId: {
    type: Number,
    default: null
  },
  parentName: {
    type: String,
    default: null
  }
})

const emit = defineEmits(["update:visible", "save"])

const saving = ref(false)

const form = ref({
  type: null,
  name: "",
  code: "",
  notes: "",
  status: true,
  propertyValues: {}
})

/** Get type properties based on the currently selected type */
const typeProperties = computed(() => {
  if (!form.value.type) return []
  return nomenclaturesStore.getTypeProperties(form.value.type)
})

const errors = ref({
  type: null,
  name: null
})

const dialogTitle = computed(() => {
  if (props.editItem) {
    return t("views.nomenclatures.dialog.editTitle")
  }
  if (props.parentId) {
    return t("views.nomenclatures.dialog.createSubItemTitle")
  }
  return t("views.nomenclatures.dialog.createTitle")
})

const typeOptions = computed(() => {
  return props.availableTypes.map((item) => ({
    label: item.label,
    value: item.value
  }))
})

const isValid = computed(() => {
  return !!form.value.type && !!form.value.name.trim()
})

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      resetForm()
    }
  }
)

watch(
  () => props.nomenclatureType,
  (type) => {
    if (type && !props.editItem) {
      form.value.type = type
    }
  },
  { immediate: true }
)

const resetForm = () => {
  errors.value = { type: null, name: null }

  if (props.editItem) {
    const meta = props.editItem.metadata ?? {}
    form.value = {
      type: props.editItem.type,
      name: props.editItem.name ?? "",
      code: props.editItem.code ?? "",
      notes: meta.notes ?? "",
      status: props.editItem.status ?? true,
      propertyValues: { ...meta }
    }
  } else {
    form.value = {
      type: props.nomenclatureType ?? null,
      name: "",
      code: "",
      notes: "",
      status: true,
      propertyValues: {}
    }
  }
}

const validate = () => {
  errors.value = { type: null, name: null }
  let valid = true

  if (!form.value.type) {
    errors.value.type = t("views.nomenclatures.dialog.validation.typeRequired")
    valid = false
  }

  if (!form.value.name.trim()) {
    errors.value.name = t("views.nomenclatures.dialog.validation.nameRequired")
    valid = false
  }

  return valid
}

const handleSave = () => {
  if (!validate()) return

  // Merge notes + property values into metadata
  const metadata = {}
  if (form.value.notes.trim()) {
    metadata.notes = form.value.notes.trim()
  }
  for (const [key, value] of Object.entries(form.value.propertyValues)) {
    if (
      key !== "notes" &&
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      metadata[key] = value
    }
  }

  const payload = {
    type: form.value.type,
    name: form.value.name.trim(),
    code: form.value.code.trim() || null,
    status: form.value.status,
    metadata: Object.keys(metadata).length > 0 ? metadata : null
  }

  if (props.parentId) {
    payload.parent_id = props.parentId
  }

  emit("save", payload)
}
</script>
